import { forwardRef, ReactNode, useEffect } from "react";
import { useRouteTransition } from "@/components/RouteTransitionContext";
import { traceRouteEvent } from "@/lib/route-debug-trace";

interface PageTransitionProps {
  children: ReactNode;
  className?: string;
}

/**
 * Wrapper de rota com fade sutil (apenas opacity, 200ms).
 *
 * Sincronia em dispositivos lentos:
 *  - Consome `token` de `RouteTransitionContext`, atualizado em
 *    `useLayoutEffect` no commit do React.
 *  - Usa `key={token}` para remontar o nó animado no MESMO commit
 *    em que `ScrollRestoration` aplica o scroll — fade e scroll
 *    iniciam no mesmo paint.
 *  - `data-route-token` expõe a flag para CSS/observers e para
 *    debug visual em devtools.
 */
const PageTransition = forwardRef<HTMLDivElement, PageTransitionProps>(
  ({ children, className }, ref) => {
    const { token, routeKey } = useRouteTransition();

    useEffect(() => {
      traceRouteEvent("PageTransition:mount", routeKey, { token });
      return () => {
        traceRouteEvent("PageTransition:unmount", routeKey, { token });
      };
    }, [routeKey, token]);

    return (
      <div
        ref={ref}
        key={token}
        data-route-token={token}
        data-route-key={routeKey}
        className={`route-transition${className ? ` ${className}` : ""}`}
      >
        {children}
      </div>
    );
  }
);

PageTransition.displayName = "PageTransition";

export default PageTransition;
